import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../utils/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import {
  Camera, Trash2, Image as ImageIcon, X, Video, ImagePlus, RefreshCw,
  CheckCircle2, AlertCircle, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { downscaleImage } from '../utils/imageDownscale';

// Iter-360 (P0-348) — Event Photo Upload.
// Cloudinary free-tier hard caps:
//   • Image: 10 MB per file
//   • Video: 100 MB per file
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const MAX_VIDEO_BYTES = 100 * 1024 * 1024;
const ACCEPTED_VIDEO = 'video/mp4,video/quicktime,video/webm,video/x-m4v,.mp4,.mov,.webm,.m4v';

// Staged upload item statuses. `pending` = queued locally; `uploading` = in
// flight; `uploaded` = success; `failed` = surfaced to user with retry.
const STATUS_PENDING = 'pending';
const STATUS_UPLOADING = 'uploading';
const STATUS_UPLOADED = 'uploaded';
const STATUS_FAILED = 'failed';

const uuid = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const JobPhotoUpload = ({ shiftId, bookingId, agencyJobId, onUploaded, requiredShots = [] }) => {
  const [photos, setPhotos] = useState([]);
  const hasShots = Array.isArray(requiredShots) && requiredShots.length > 0;
  const [activeShot, setActiveShot] = useState(hasShots ? requiredShots[0] : null);
  // Iter-360 (P0-348) — Staging tray. Failed uploads stay here so the
  // user can retry WITHOUT re-picking the files.
  const [tray, setTray] = useState([]); // [{id, file, kind, status, error, previewUrl, progress}]
  const [showGallery, setShowGallery] = useState(false);
  const trayRef = useRef(tray);
  trayRef.current = tray;

  const refType = agencyJobId ? 'agency_job' : bookingId ? 'booking' : 'shift';
  const refId = agencyJobId || bookingId || shiftId;

  const fetchPhotos = useCallback(async () => {
    if (!refId) return;
    try {
      const res = await api.get(`/profile/job-photos/${refType}/${refId}`);
      setPhotos(res.data);
    } catch (error) {
      console.error('Failed to load job photos:', error);
    }
  }, [refType, refId]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  // Iter-360 — Turn a picked FileList into staging tray entries.
  const enqueueFiles = async (fileList, kind) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (files.length === 0) return;
    const additions = [];
    for (let file of files) {
      // Mobile camera photos are 6-15MB — downscale before size check/upload.
      if (kind !== 'video') file = await downscaleImage(file);
      const maxBytes = kind === 'video' ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
      if (file.size > maxBytes) {
        toast.error(
          `${file.name} exceeds the ${kind === 'video' ? '100 MB' : '10 MB'} limit and was skipped.`,
        );
        continue;
      }
      additions.push({
        id: uuid(),
        file,
        kind,
        status: STATUS_PENDING,
        error: null,
        previewUrl: URL.createObjectURL(file),
        progress: 0,
        attempts: 0,
        shotCategory: hasShots ? activeShot : null,
      });
    }
    if (additions.length > 0) {
      setTray((prev) => [...prev, ...additions]);
      // Uploads auto-start from the tray-watcher effect below (fires after the
      // state commits) — a setTimeout kick here raced the state flush on slower
      // mobile devices and left items stuck on "Queued".
    }
  };

  // Iter-360 — Upload a single tray item. Never removes the item on
  // failure — the user must retry or explicitly remove.
  const uploadOne = async (item) => {
    // Mark uploading.
    setTray((prev) => prev.map((t) => (t.id === item.id ? { ...t, status: STATUS_UPLOADING, error: null, progress: 5 } : t)));
    try {
      const resourceType = item.kind === 'video' ? 'video' : 'image';
      const sigRes = await api.post('/cloudinary/signature', {
        folder: 'ieventur/job-photos',
        resource_type: resourceType,
      });
      const { signature, timestamp, cloud_name, api_key } = sigRes.data;

      const formData = new FormData();
      formData.append('file', item.file);
      formData.append('signature', signature);
      formData.append('timestamp', timestamp);
      formData.append('api_key', api_key);
      formData.append('folder', 'ieventur/job-photos');

      // Use XMLHttpRequest for real progress updates (fetch has no
      // native upload-progress event; we want per-file % feedback).
      const uploadData = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `https://api.cloudinary.com/v1_1/${cloud_name}/${resourceType}/upload`);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 90) + 5; // 5-95%
            setTray((prev) => prev.map((t) => (t.id === item.id ? { ...t, progress: pct } : t)));
          }
        };
        xhr.onload = () => {
          try {
            const data = JSON.parse(xhr.responseText || '{}');
            if (xhr.status >= 200 && xhr.status < 300 && data.secure_url) resolve(data);
            else reject(new Error(data?.error?.message || `Upload failed (${xhr.status})`));
          } catch (e) {
            reject(new Error('Upload response invalid'));
          }
        };
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.ontimeout = () => reject(new Error('Upload timed out'));
        xhr.timeout = 90 * 1000; // 90s per file — fail fast so auto-retry kicks in
        xhr.send(formData);
      });

      await api.post('/profile/job-photos', {
        photo_url: uploadData.secure_url,
        thumbnail_url: item.kind === 'video'
          ? (uploadData.secure_url.replace(/\.(mp4|mov|webm|m4v)$/i, '.jpg'))
          : null,
        media_type: item.kind,
        caption: null,
        shift_id: shiftId || null,
        booking_id: bookingId || null,
        agency_job_id: agencyJobId || null,
        shot_category: item.shotCategory || null,
      });

      setTray((prev) => prev.map((t) => (t.id === item.id ? { ...t, status: STATUS_UPLOADED, progress: 100 } : t)));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[JobPhotoUpload] upload failed:', error);
      // Prefer the backend's friendly message (e.g. "shift no longer exists")
      // over axios's raw "Request failed with status code 400".
      const detail = error?.response?.data?.detail;
      const msg = (detail && (detail.message || (typeof detail === 'string' ? detail : null)))
        || error?.message
        || 'Upload failed';
      // A 4xx from our save endpoint is a permanent rejection (bad reference,
      // validation) — retrying can't fix it, so fail fast without burning retries.
      const savedStatus = error?.response?.status;
      const permanent = typeof savedStatus === 'number' && savedStatus >= 400 && savedStatus < 500;
      // Permanently rejected (shift closed / bad reference) — alert the
      // coordinators once so they can reopen the shift or follow up.
      if (permanent && bookingId && !blockedReportedRef.current) {
        blockedReportedRef.current = true;
        api.post('/profile/job-photos/blocked', { booking_id: bookingId, reason: msg }).catch(() => {});
      }
      const attempts = (item.attempts || 0) + 1;
      const MAX_AUTO_RETRIES = 3;
      if (!permanent && attempts < MAX_AUTO_RETRIES) {
        // Auto-retry with brief backoff — re-queue as pending so the
        // sequential processor picks it up again (no user action needed).
        setTray((prev) => prev.map((t) => (t.id === item.id
          ? { ...t, status: STATUS_PENDING, attempts, progress: 0, error: `Retrying (${attempts}/${MAX_AUTO_RETRIES})…` }
          : t)));
        await new Promise((r) => setTimeout(r, 1200 * attempts));
      } else {
        setTray((prev) => prev.map((t) => (t.id === item.id
          ? { ...t, status: STATUS_FAILED, attempts, error: msg, progress: 0, permanent }
          : t)));
      }
    }
  };

  // Iter-360 — Sequential processor. Runs pending items one at a time so
  // slow mobile networks don't OOM on parallel Cloudinary streams.
  const processingRef = useRef(false);
  const blockedReportedRef = useRef(false);
  const processTray = async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      // Loop until no pending items remain.
      while (true) {
        const next = trayRef.current.find((t) => t.status === STATUS_PENDING);
        if (!next) break;
        // eslint-disable-next-line no-await-in-loop
        await uploadOne(next);
      }
      // Any successful uploads → refresh the gallery + auto-clear
      // successful tray entries so the UI doesn't get cluttered.
      const anyUploaded = trayRef.current.some((t) => t.status === STATUS_UPLOADED);
      if (anyUploaded) {
        await fetchPhotos();
        try { onUploaded?.(); } catch { /* non-critical */ }
        setTimeout(() => {
          setTray((prev) => prev.filter((t) => t.status !== STATUS_UPLOADED));
        }, 1500);
      }
    } finally {
      processingRef.current = false;
    }
  };

  // Tray watcher — reliably drives the sequential uploader. Runs after every
  // tray commit, so newly-queued items (and auto-retry re-queues) always get
  // picked up regardless of mobile state-flush timing. Guarded by
  // processingRef so it never double-runs.
  useEffect(() => {
    if (!processingRef.current && tray.some((t) => t.status === STATUS_PENDING)) {
      processTray();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tray]);

  // Mobile safety net — talent often switch to Messages/Camera mid-upload,
  // which suspends in-flight work. When the app returns to the foreground,
  // resume any pending uploads that aren't already processing.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible'
          && !processingRef.current
          && trayRef.current.some((t) => t.status === STATUS_PENDING)) {
        processTray();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retryItem = (id) => {
    setTray((prev) => prev.map((t) => (t.id === id ? { ...t, status: STATUS_PENDING, error: null, progress: 0, attempts: 0 } : t)));
    setTimeout(() => processTray(), 0);
  };

  const removeItem = (id) => {
    setTray((prev) => {
      const target = prev.find((t) => t.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((t) => t.id !== id);
    });
  };

  const retryAllFailed = () => {
    setTray((prev) => prev.map((t) => (t.status === STATUS_FAILED ? { ...t, status: STATUS_PENDING, error: null, progress: 0 } : t)));
    setTimeout(() => processTray(), 0);
  };

  const anyUploading = tray.some((t) => t.status === STATUS_UPLOADING || t.status === STATUS_PENDING);
  const failedCount = tray.filter((t) => t.status === STATUS_FAILED).length;

  const handleDelete = async (photoId) => {
    try {
      await api.delete(`/profile/job-photos/${photoId}`);
      toast.success('Photo removed');
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div data-testid="job-photo-upload">
      {hasShots && (
        <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-2.5" data-testid="shot-selector">
          <p className="text-[11px] font-semibold text-slate-600 mb-1.5">
            Tag your next photos as: <span className="text-slate-400 font-normal">(the client needs each shot below)</span>
          </p>
          <div className="flex flex-wrap gap-1.5">
            {requiredShots.map((s) => {
              const covered = photos.some((p) => (p.shot_category || '').toLowerCase() === s.toLowerCase());
              const active = activeShot === s;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setActiveShot(s)}
                  className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition flex items-center gap-1 ${active ? 'bg-indigo-600 text-white border-indigo-600' : covered ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-white text-slate-600 border-slate-200'}`}
                  data-testid={`shot-chip-${s}`}
                >
                  {covered && <span>✓</span>}{s}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {/* Iter-360 (P0-348) — Split pickers. Camera opens the device
          camera; Gallery opens the picker. Both accept multi-select. */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <label className="cursor-pointer">
          <input
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            onChange={(e) => { enqueueFiles(e.target.files, 'image'); e.target.value = ''; }}
            data-testid="job-photo-camera-input"
          />
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-cyan-300 text-cyan-700 hover:bg-cyan-50 transition-colors">
            <Camera className="w-4 h-4" />
            <span className="text-sm font-medium">Take Photo</span>
          </div>
        </label>
        <label className="cursor-pointer">
          <input
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => { enqueueFiles(e.target.files, 'image'); e.target.value = ''; }}
            data-testid="job-photo-gallery-input"
          />
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50 transition-colors">
            <ImagePlus className="w-4 h-4" />
            <span className="text-sm font-medium">Choose from Gallery</span>
          </div>
        </label>
        <label className="cursor-pointer">
          <input
            type="file"
            accept={ACCEPTED_VIDEO}
            multiple
            className="hidden"
            onChange={(e) => { enqueueFiles(e.target.files, 'video'); e.target.value = ''; }}
            data-testid="job-video-file-input"
          />
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-purple-300 text-purple-700 hover:bg-purple-50 transition-colors">
            <Video className="w-4 h-4" />
            <span className="text-sm font-medium">Upload Video</span>
          </div>
        </label>
        {photos.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowGallery(true)}
            className="text-gray-500"
            data-testid="view-photos-btn"
          >
            <ImageIcon className="w-4 h-4 mr-1" />
            {photos.length} photo{photos.length !== 1 ? 's' : ''}
          </Button>
        )}
      </div>

      {/* Iter-360 — Upload tray. Persists failed items with a Retry
          button so a partial-failure never wipes the user's selection. */}
      {tray.length > 0 && (
        <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-2" data-testid="upload-tray">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-slate-700">
              Upload queue · {tray.length}
              {anyUploading && ' · uploading…'}
              {failedCount > 0 && ` · ${failedCount} failed`}
            </p>
            {failedCount > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
                onClick={retryAllFailed}
                data-testid="retry-all-failed-btn"
              >
                <RefreshCw className="w-3 h-3 mr-1" /> Retry all
              </Button>
            )}
          </div>
          {failedCount > 0 && (
            <div className="mb-2 rounded-md bg-rose-50 border border-rose-200 px-2.5 py-1.5 text-[11px] text-rose-800" data-testid="upload-failure-tip">
              {tray.some((t) => t.status === STATUS_FAILED && t.permanent) ? (
                <span>Some photos couldn't be saved — this shift may be closed or no longer accepting photos. Please contact your coordinator.</span>
              ) : (
                <span>Upload trouble — check your signal or switch to Wi-Fi, then tap <strong>Retry all</strong>. Your selection is saved.</span>
              )}
            </div>
          )}
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {tray.map((t) => (
              <div
                key={t.id}
                className={`relative group rounded-md overflow-hidden border ${
                  t.status === STATUS_FAILED ? 'border-rose-400' :
                  t.status === STATUS_UPLOADED ? 'border-emerald-400' :
                  'border-slate-200'
                } aspect-square bg-white`}
                data-testid={`tray-item-${t.status}`}
              >
                {t.kind === 'video' ? (
                  <video src={t.previewUrl} className="w-full h-full object-cover" muted />
                ) : (
                  <img src={t.previewUrl} alt="" className="w-full h-full object-cover" />
                )}
                {/* Status overlay */}
                <div className="absolute inset-0 bg-black/25 flex items-center justify-center">
                  {t.status === STATUS_PENDING && <span className="text-white text-[11px] font-semibold">Queued</span>}
                  {t.status === STATUS_UPLOADING && (
                    <div className="text-center text-white">
                      <Loader2 className="w-5 h-5 mx-auto animate-spin" />
                      <div className="text-[11px] font-semibold mt-1">{t.progress}%</div>
                    </div>
                  )}
                  {t.status === STATUS_UPLOADED && <CheckCircle2 className="w-6 h-6 text-emerald-300" />}
                  {t.status === STATUS_FAILED && (
                    <div className="text-center px-1">
                      <AlertCircle className="w-5 h-5 mx-auto text-rose-300" />
                      <div className="text-[10px] font-semibold text-white truncate">
                        {t.error?.slice(0, 40) || 'Failed'}
                      </div>
                    </div>
                  )}
                </div>
                {/* Actions */}
                {(t.status === STATUS_FAILED || t.status === STATUS_PENDING) && (
                  <button
                    onClick={() => removeItem(t.id)}
                    className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5 shadow"
                    aria-label="Remove"
                    data-testid={`tray-remove-${t.id}`}
                  >
                    <X className="w-3.5 h-3.5 text-slate-700" />
                  </button>
                )}
                {t.status === STATUS_FAILED && (
                  <button
                    onClick={() => retryItem(t.id)}
                    className="absolute bottom-1 right-1 bg-rose-600 text-white rounded-md px-1.5 py-0.5 text-[10px] font-semibold shadow inline-flex items-center gap-1"
                    data-testid={`tray-retry-${t.id}`}
                  >
                    <RefreshCw className="w-3 h-3" /> Retry
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Uploaded gallery strip */}
      {photos.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {photos.slice(0, 5).map((photo) => {
            const isVideo = photo.media_type === 'video' || /\.(mp4|mov|webm|m4v)$/i.test(photo.photo_url || '');
            return (
              <div
                key={photo.id}
                className="w-16 h-16 rounded-lg overflow-hidden shrink-0 cursor-pointer border border-gray-200 relative"
                onClick={() => setShowGallery(true)}
              >
                {isVideo ? (
                  <>
                    <video src={photo.photo_url} className="w-full h-full object-cover" muted preload="metadata" />
                    <div className="absolute inset-0 grid place-items-center bg-black/30 pointer-events-none">
                      <Video className="w-4 h-4 text-white" />
                    </div>
                  </>
                ) : (
                  <img src={photo.photo_url} alt="" className="w-full h-full object-cover" />
                )}
              </div>
            );
          })}
          {photos.length > 5 && (
            <div
              className="w-16 h-16 rounded-lg bg-gray-100 shrink-0 flex items-center justify-center cursor-pointer"
              onClick={() => setShowGallery(true)}
            >
              <span className="text-xs text-gray-500">+{photos.length - 5}</span>
            </div>
          )}
        </div>
      )}

      {/* Full Gallery Dialog */}
      <Dialog open={showGallery} onOpenChange={setShowGallery}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Event Photos ({photos.length})</DialogTitle>
            <DialogDescription className="sr-only">Uploaded event photos and videos.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3 py-4">
            {photos.map((photo) => {
              const isVideo = photo.media_type === 'video' || /\.(mp4|mov|webm|m4v)$/i.test(photo.photo_url || '');
              return (
                <div key={photo.id} className="relative group rounded-lg overflow-hidden aspect-square" data-testid={isVideo ? 'job-video' : 'job-photo'}>
                  {isVideo ? (
                    <video src={photo.photo_url} className="w-full h-full object-cover" controls preload="metadata" />
                  ) : (
                    <img src={photo.photo_url} alt={photo.caption || ''} className="w-full h-full object-cover" />
                  )}
                  <div className="absolute inset-0 bg-gray-50/0 group-hover:bg-gray-50/30 transition-all flex items-end pointer-events-none">
                    <div className="w-full p-2 opacity-0 group-hover:opacity-100 transition-opacity flex justify-between items-center pointer-events-auto">
                      {photo.caption && <span className="text-gray-900 text-xs truncate">{photo.caption}</span>}
                      <Button
                        size="sm" variant="destructive" className="h-6 w-6 p-0 ml-auto"
                        onClick={() => handleDelete(photo.id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobPhotoUpload;
